import { Link } from "react-router-dom";
import { StudentLayout } from "@jambushrushti/components/learning/StudentLayout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Progress } from "@jambushrushti/components/ui/progress";
import { useEnrollments, useCourses } from "@jambushrushti/hooks/useLMS";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  BookOpen,
  Calendar,
  GraduationCap,
  PlayCircle,
  Clock,
  ArrowRight,
  Sparkles,
  Trophy,
  Target,
  Video,
  CheckCircle2,
  TrendingUp,
  Users,
  FileText,
  Star,
} from "lucide-react";
import { Skeleton } from "@jambushrushti/components/ui/skeleton";
import { format, isAfter, isBefore, addDays } from "date-fns";

// Mock upcoming lectures data
const upcomingLectures = [
  {
    id: "1",
    title: "Introduction to Jain Agam Literature",
    course: "Jain Philosophy Basics",
    date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
    type: "Online",
    instructor: "Acharya Gurudev",
    duration: "60 mins",
  },
  {
    id: "2",
    title: "Understanding Anekantavada",
    course: "Advanced Jain Philosophy",
    date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    type: "Offline",
    venue: "MJRC Hall, Ahmedabad",
    duration: "90 mins",
  },
  {
    id: "3",
    title: "Manuscript Preservation Techniques",
    course: "Manuscript Studies",
    date: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
    type: "Online",
    instructor: "Dr. Sharma",
    duration: "45 mins",
  },
];

// Mock achievements
const achievements = [
  {
    label: "Courses Completed",
    value: 3,
    icon: Trophy,
    color: "text-amber-500",
  },
  {
    label: "Lectures Watched",
    value: 24,
    icon: PlayCircle,
    color: "text-emerald-500",
  },
  { label: "Hours Learned", value: 18, icon: Clock, color: "text-blue-500" },
  {
    label: "Assignments Done",
    value: 8,
    icon: CheckCircle2,
    color: "text-violet-500",
  },
];

export default function StudentDashboard() {
  const { user } = useAdminAuth();
  const { data: enrollments = [], isLoading: enrollmentsLoading } =
    useEnrollments();
  const { data: allCourses = [], isLoading: coursesLoading } = useCourses();

  // Calculate stats
  const enrolledCount = enrollments.length;
  const completedCount = enrollments.filter(
    (e: any) => e.status === "completed"
  ).length;
  const inProgressCount = enrollments.filter(
    (e: any) => e.status === "active"
  ).length;

  // Get recommended courses (not enrolled)
  const enrolledCourseIds = enrollments.map((e: any) => e.course_id);
  const recommendedCourses = allCourses
    .filter(course => !enrolledCourseIds.includes(course.id))
    .slice(0, 3);

  return (
    <StudentLayout title="My Learning">
      <Seo
        title="Student Dashboard | Jambushrusti Learning Portal"
        description="Track your learning progress, enrolled courses, and upcoming lectures."
      />

      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="relative overflow-hidden rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-8">
          {/* Decorative Elements */}
          <div className="absolute right-0 top-0 h-72 w-72 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-48 w-48 -translate-x-1/2 translate-y-1/2 rounded-full bg-emerald-500/5 blur-2xl" />

          <div className="relative z-10">
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Badge className="border-primary/20 bg-primary/10 font-medium text-primary">
                    <Sparkles className="mr-1 h-3 w-3" />
                    Student Portal
                  </Badge>
                </div>
                <h1 className="font-heading mb-2 text-3xl font-bold text-foreground md:text-4xl">
                  Welcome back, {user?.name?.split(" ")[0] || "Student"}!
                </h1>
                <p className="max-w-xl text-lg text-muted-foreground">
                  Continue your learning journey. You have {inProgressCount}{" "}
                  course{inProgressCount !== 1 ? "s" : ""} in progress.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  asChild
                  size="lg"
                  className="gap-2 rounded-xl shadow-lg shadow-primary/20"
                >
                  <Link to="/learning/courses">
                    <BookOpen className="h-5 w-5" />
                    Browse Courses
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Achievement Stats */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {achievements.map(stat => (
            <Card
              key={stat.label}
              className="group overflow-hidden rounded-2xl border-border transition-all duration-300 hover:shadow-lg"
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div
                    className={`rounded-xl bg-muted p-3.5 transition-transform duration-300 group-hover:scale-110`}
                  >
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground">
                      {stat.value}
                    </p>
                    <p className="text-sm font-medium text-muted-foreground">
                      {stat.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Enrolled Courses */}
          <div className="lg:col-span-2">
            <Card className="h-full rounded-2xl border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2 text-xl">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    My Enrolled Courses
                  </CardTitle>
                  <CardDescription>Continue where you left off</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="rounded-xl"
                >
                  <Link to="/learning/courses">
                    View All
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                {enrollmentsLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="flex gap-4 rounded-xl bg-muted/30 p-4"
                      >
                        <Skeleton className="h-16 w-24 rounded-lg" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                          <Skeleton className="h-2 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : enrollments.length > 0 ? (
                  <div className="space-y-4">
                    {enrollments.slice(0, 4).map((enrollment: any) => {
                      const course = enrollment.course;
                      const progress = Math.floor(Math.random() * 80) + 10; // Mock progress

                      return (
                        <div
                          key={enrollment.id}
                          className="group flex items-center gap-4 rounded-xl border border-transparent bg-muted/30 p-4 transition-all duration-300 hover:border-primary/20 hover:bg-muted/50"
                        >
                          <div className="flex h-16 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/10">
                            {course?.thumbnail_url ? (
                              <img
                                src={course.thumbnail_url}
                                alt={course.title}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <BookOpen className="h-8 w-8 text-primary/60" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate font-semibold text-foreground transition-colors group-hover:text-primary">
                              {course?.title || "Course"}
                            </h4>
                            <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Video className="h-3.5 w-3.5" />
                                {course?.course_mode}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {course?.duration || "Self-paced"}
                              </span>
                            </div>
                            <div className="mt-3 flex items-center gap-3">
                              <Progress
                                value={progress}
                                className="h-2 flex-1"
                              />
                              <span className="text-sm font-medium text-primary">
                                {progress}%
                              </span>
                            </div>
                          </div>
                          <Button size="sm" asChild className="rounded-lg">
                            <Link to={`/learning/courses/${course?.id}`}>
                              <PlayCircle className="mr-1 h-4 w-4" />
                              Continue
                            </Link>
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-border bg-muted/30 py-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                      <GraduationCap className="h-8 w-8 text-primary/60" />
                    </div>
                    <h4 className="font-heading mb-2 text-lg font-semibold text-foreground">
                      No enrolled courses yet
                    </h4>
                    <p className="mb-4 text-muted-foreground">
                      Start your learning journey today!
                    </p>
                    <Button className="rounded-xl" asChild>
                      <Link to="/learning/courses">
                        <BookOpen className="mr-2 h-4 w-4" />
                        Explore Courses
                      </Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Lectures */}
          <div>
            <Card className="h-full rounded-2xl border-border">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-xl">
                  <Calendar className="h-5 w-5 text-primary" />
                  Upcoming Lectures
                </CardTitle>
                <CardDescription>
                  Don't miss your scheduled sessions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {upcomingLectures.map((lecture, index) => (
                  <div
                    key={lecture.id}
                    className="rounded-xl border-l-4 border-primary bg-muted/30 p-4 transition-colors hover:bg-muted/50"
                  >
                    <h4 className="line-clamp-2 text-sm font-medium leading-snug text-foreground">
                      {lecture.title}
                    </h4>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {lecture.course}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          lecture.type === "Online"
                            ? "border-emerald-200 bg-emerald-500/10 text-emerald-700"
                            : "border-blue-200 bg-blue-500/10 text-blue-700"
                        }`}
                      >
                        {lecture.type}
                      </Badge>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {lecture.duration}
                      </span>
                    </div>
                    <p className="mt-2 text-xs font-medium text-primary">
                      {format(lecture.date, "EEE, MMM d 'at' h:mm a")}
                    </p>
                  </div>
                ))}

                <Button variant="outline" className="w-full rounded-xl" asChild>
                  <Link to="/learning/schedule">
                    View Full Schedule
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recommended Courses */}
        {recommendedCourses.length > 0 && (
          <Card className="rounded-2xl border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="font-heading flex items-center gap-2 text-xl">
                  <Star className="h-5 w-5 text-amber-500" />
                  Recommended For You
                </CardTitle>
                <CardDescription>
                  Expand your knowledge with these courses
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                asChild
                className="rounded-xl"
              >
                <Link to="/learning/courses">
                  Browse All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {recommendedCourses.map(course => (
                  <Link key={course.id} to={`/learning/courses/${course.id}`}>
                    <div className="group rounded-xl border border-transparent bg-muted/30 p-4 transition-all duration-300 hover:border-primary/20 hover:bg-muted/50 hover:shadow-lg">
                      <div className="mb-4 flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-primary/10">
                        {course.thumbnail_url ? (
                          <img
                            src={course.thumbnail_url}
                            alt={course.title}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <BookOpen className="h-10 w-10 text-primary/40" />
                        )}
                      </div>
                      <div className="mb-2 flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {course.subject}
                        </Badge>
                        <Badge className="border-primary/20 bg-primary/10 text-xs text-primary">
                          {course.level}
                        </Badge>
                      </div>
                      <h4 className="line-clamp-2 font-semibold text-foreground transition-colors group-hover:text-primary">
                        {course.title}
                      </h4>
                      {course.instructor_name && (
                        <p className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
                          <Users className="h-3.5 w-3.5" />
                          {course.instructor_name}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              label: "My Assignments",
              href: "/learning/my-assignments",
              icon: FileText,
              description: "View pending tasks",
            },
            {
              label: "Study Materials",
              href: "/learning/my-materials",
              icon: BookOpen,
              description: "Access resources",
            },
            {
              label: "My Quizzes",
              href: "/learning/my-quizzes",
              icon: TrendingUp,
              description: "Take assessments",
            },
            {
              label: "My Schedule",
              href: "/learning/my-schedule",
              icon: Target,
              description: "View calendar",
            },
          ].map(item => (
            <Link key={item.label} to={item.href}>
              <Card className="group h-full cursor-pointer overflow-hidden rounded-2xl border-border transition-all duration-300 hover:border-primary/40 hover:shadow-xl">
                <CardContent className="relative flex flex-col items-center p-6 text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative z-10">
                    <div className="mb-4 rounded-2xl bg-primary/10 p-4 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground">
                      <item.icon className="h-7 w-7" />
                    </div>
                    <h3 className="font-semibold text-foreground">
                      {item.label}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </StudentLayout>
  );
}
